// Reports Hub — curated landing for the entire reporting surface.
// Phase 6.7 redesign: thematic sections + live hero stat instead of flat grid.

import type React from 'react';
import { useMemo, useState } from 'react';
import { AppShell } from '@/components/AppShell';
import { useAuthStore } from '@/store/auth';
import { useChaiBrief } from '@/api/reports';
import { Money } from '@/components/primitives/Money';
import { KbdChip } from '@/components/primitives/KbdChip';
import { fiscalYearForDate } from '@/utils/format';
import { cn } from '@/lib/cn';

interface Tile {
  title: string;
  description: string;
  href: string;
  shortcut?: string;
  flag?: 'live' | 'beta' | 'soon';
}

const FINANCIALS: Tile[] = [
  { title: 'Trial Balance',  description: 'Every ledger\'s closing position — Dr ≡ Cr balance check', href: '/reports/trial-balance', shortcut: 'Alt+T', flag: 'live' },
  { title: 'Profit & Loss',  description: 'Revenue vs Expense for the period', href: '/reports/pnl', shortcut: 'Alt+L', flag: 'live' },
  { title: 'Balance Sheet',  description: 'Assets, Liabilities, Period Profit', href: '/reports/balance-sheet', shortcut: 'Alt+B', flag: 'live' },
  { title: 'Cash Flow',      description: 'Schedule III · Operating / Investing / Financing', href: '/reports/cash-flow', shortcut: 'Alt+C', flag: 'live' },
  { title: 'Company comparison', description: 'Put 2–9 companies side by side — P&L / BS / TB / Sales, year over year', href: '/reports/comparison', flag: 'live' },
];

const STATUTORY: Tile[] = [
  { title: 'GSTR-1',         description: 'Outward supplies summary + JSON export', href: '/reports/gstr-1', shortcut: 'Alt+G', flag: 'live' },
  { title: 'GSTR-3B',        description: 'Monthly summary — outward + ITC + net payable', href: '/reports/gstr-3b', shortcut: 'Alt+3', flag: 'live' },
  { title: 'MSME 43B(h)',    description: 'Micro/Small payables aging — disallowance exposure', href: '/reports/msme-payables', shortcut: 'Alt+M', flag: 'live' },
  { title: 'TDS Liability',  description: 'Vendor-wise TDS exposure · 194A/C/H/I/J/Q · 206AA-aware', href: '/reports/tds-liability', shortcut: 'Alt+D', flag: 'live' },
];

const SUBLEDGER: Tile[] = [
  { title: 'Outstanding',    description: 'Receivables + payables with ageing buckets', href: '/reports/outstanding', shortcut: 'Alt+O', flag: 'live' },
  { title: 'Party Statement',description: 'Ledger confirmation — activity + open bills per party', href: '/reports/party-statement', shortcut: 'Alt+Y', flag: 'live' },
  { title: 'Group Summary',  description: 'Roll up any chart group → its ledgers + subgroups', href: '/reports/group-summary', shortcut: 'Alt+J', flag: 'live' },
];

const BOOKS: Tile[] = [
  { title: 'Sales Register',    description: 'Every sales invoice with party + GST split', href: '/reports/sales-register', shortcut: 'Alt+Shift+I', flag: 'live' },
  { title: 'Purchase Register', description: 'Every purchase bill with party + GST split', href: '/reports/purchase-register', shortcut: 'Alt+U', flag: 'live' },
  { title: 'CN Register',       description: 'Credit notes (sales returns) with GST split', href: '/reports/credit-note-register', flag: 'live' },
  { title: 'DN Register',       description: 'Debit notes (purchase returns) with GST split', href: '/reports/debit-note-register', flag: 'live' },
];

const INVENTORY: Tile[] = [
  { title: 'Stock Summary',  description: 'Per-item opening + in + out + closing', href: '/reports/stock-summary', shortcut: 'Alt+S', flag: 'live' },
  { title: 'Reorder Report', description: 'Items at/below reorder level with suggested qty', href: '/reports/reorder', shortcut: 'Alt+R', flag: 'live' },
];

export function ReportsHubPage() {
  const tenant = useAuthStore((s) => s.tenant);
  const brief = useChaiBrief();
  const d = brief.data;
  const today = new Date();

  // Quick filter across every tile by title / description / shortcut
  const [q, setQ] = useState('');
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return null;
    const test = (t: Tile) =>
      t.title.toLowerCase().includes(s) ||
      t.description.toLowerCase().includes(s) ||
      (t.shortcut ?? '').toLowerCase().includes(s);
    return {
      financials: FINANCIALS.filter(test),
      statutory: STATUTORY.filter(test),
      subledger: SUBLEDGER.filter(test),
      books: BOOKS.filter(test),
      inventory: INVENTORY.filter(test),
    };
  }, [q]);
  const totalMatchCount = matches
    ? matches.financials.length + matches.statutory.length + matches.subledger.length + matches.books.length + matches.inventory.length
    : 0;

  return (
    <AppShell
      pageHeader={
        <div className="flex flex-wrap items-baseline gap-3">
          <h1 className="font-display text-display-md font-semibold text-obsidian" style={{ letterSpacing: 'var(--tracking-display)' }}>
            Reports
          </h1>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…  (try 'gst', 'aging', 'msme')"
            className="hidden min-w-[260px] flex-1 rounded-md border border-mist bg-haze px-3 py-1.5 text-body-sm focus:border-cobalt/60 focus:outline-none focus:shadow-focus md:block"
          />
          <div className="ml-auto flex items-center gap-2">
            <a href="/bi" className="rounded-md border border-mist bg-haze px-3 py-1.5 text-body-sm text-obsidian hover:border-emerald hover:text-emerald">
              BI dashboards
            </a>
            <a href="/reports-catalogue" className="rounded-md border border-mist bg-haze px-3 py-1.5 text-body-sm text-obsidian hover:border-citrus hover:text-obsidian">
              Full catalogue
            </a>
          </div>
        </div>
      }
    >
      <div>
        <div className="mb-4 hidden">{fiscalYearForDate(today)} · {tenant?.name}</div>
        {/* Hero · Day Book with live numbers */}
        <section className="mb-12">
          <SectionLabel kind="cobalt" label="Today" hint="Always-on snapshot of the open day" />
          <a
            href="/reports/day-book"
            className="group relative mt-4 block overflow-hidden rounded-md border border-mist bg-frost p-6 transition hover:border-cobalt hover:shadow-card sm:p-8"
            style={{ backgroundImage: 'var(--wash-hero)' }}
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="font-display text-display-lg font-semibold text-obsidian">Day Book</h2>
                  <KbdChip>Alt+K</KbdChip>
                </div>
                <p className="mt-2 max-w-md text-body-md text-slate">
                  Every voucher entered today (or any date) with full filtering, search, CSV export, and a one-click drill into voucher detail.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-x-6 gap-y-2 text-right">
                <HeroStat label="Vouchers today" value={(d?.vouchersToday ?? 0).toLocaleString('en-IN')} />
                <HeroStat label="Collections" value={<Money amount={d?.todaysCollections ?? 0} important />} />
                <HeroStat label="Cash on hand" value={<Money amount={d?.cashOnHand ?? 0} important />} />
              </div>
            </div>
            <div className="absolute right-6 top-1/2 -translate-y-1/2 hidden text-cool transition group-hover:text-cobalt sm:block">
              <span className="text-display-md">→</span>
            </div>
          </a>
        </section>

        {matches && totalMatchCount === 0 && (
          <div className="mb-10 rounded-md border border-mist bg-frost p-6 text-center">
            <p className="text-body-md text-slate">No reports match &ldquo;{q}&rdquo;.</p>
            <button type="button" onClick={() => setQ('')} className="mt-2 text-caption text-cobalt hover:underline">Clear search</button>
          </div>
        )}
        {matches && totalMatchCount > 0 && (
          <p className="mb-4 text-caption text-cool">{totalMatchCount} match{totalMatchCount === 1 ? '' : 'es'} for &ldquo;{q}&rdquo;</p>
        )}

        {/* Financials */}
        {(!matches || matches.financials.length > 0) && (
          <Section label="Financials" kind="cobalt" hint="Owner-grade ledgers, profitability, balance">
            <div className="orz-stagger grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {(matches?.financials ?? FINANCIALS).map((t) => <TileCard key={t.title} {...t} />)}
            </div>
          </Section>
        )}

        {/* GST + Statutory */}
        {(!matches || matches.statutory.length > 0) && (
          <Section label="GST + Statutory" kind="cobalt" hint="Filing-ready outputs · MCA compliance · Income Tax exposure">
            <div className="orz-stagger grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {(matches?.statutory ?? STATUTORY).map((t) => <TileCard key={t.title} {...t} />)}
            </div>
          </Section>
        )}

        {/* Subledger */}
        {(!matches || matches.subledger.length > 0) && (
          <Section label="Subledger" kind="cobalt" hint="Per-party + per-group drill-downs">
            <div className="orz-stagger grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {(matches?.subledger ?? SUBLEDGER).map((t) => <TileCard key={t.title} {...t} />)}
            </div>
          </Section>
        )}

        {/* Books */}
        {(!matches || matches.books.length > 0) && (
          <Section label="Books" kind="citrus" hint="Voucher-level registers for audit + GSTR cross-check">
            <div className="orz-stagger grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {(matches?.books ?? BOOKS).map((t) => <TileCard key={t.title} {...t} />)}
            </div>
          </Section>
        )}

        {/* Inventory */}
        {(!matches || matches.inventory.length > 0) && (
          <Section label="Inventory" kind="citrus" hint="Stock health + procurement triggers">
            <div className="orz-stagger grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-2">
              {(matches?.inventory ?? INVENTORY).map((t) => <TileCard key={t.title} {...t} />)}
            </div>
          </Section>
        )}

        <footer className="mt-12 flex flex-col items-start gap-1 border-t border-mist pt-6 text-caption text-cool sm:flex-row sm:items-center sm:justify-between">
          <span>{FINANCIALS.length + STATUTORY.length + SUBLEDGER.length + BOOKS.length + INVENTORY.length + 1} curated reports — all live</span>
          <a href="/reports-catalogue" className="text-cobalt hover:underline">Need a different cut? Reports Catalogue → 300+ ready reports</a>
        </footer>
      </div>
    </AppShell>
  );
}

function Section({ label, kind, hint, children }: { label: string; kind: 'cobalt' | 'citrus'; hint: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <SectionLabel kind={kind} label={label} hint={hint} />
      <div className="mt-4">{children}</div>
    </section>
  );
}

function SectionLabel({ label, kind, hint }: { label: string; kind: 'cobalt' | 'citrus'; hint?: string }) {
  const bar = kind === 'citrus' ? 'bg-citrus' : 'bg-cobalt';
  return (
    <div className="flex items-baseline gap-3">
      <h3 className="flex items-center text-micro font-medium uppercase tracking-[0.16em] text-obsidian">
        <span className={cn('mr-3 inline-block h-[2px] w-6', bar)} />
        {label}
      </h3>
      {hint && <span className="hidden text-caption text-cool sm:inline">· {hint}</span>}
    </div>
  );
}

function TileCard({ title, description, href, shortcut }: Tile) {
  return (
    <a
      href={href}
      className={cn(
        'group flex flex-col gap-2 rounded-md border border-mist bg-haze p-4 transition-all duration-150 ease-out',
        'hover:border-cobalt hover:-translate-y-0.5 hover:shadow-card focus:outline-none focus:ring-2 focus:ring-cobalt/40',
      )}
      style={{ backgroundImage: 'var(--wash-card)' }}
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-display text-body-lg font-semibold text-obsidian">{title}</h4>
        {shortcut && <KbdChip>{shortcut}</KbdChip>}
      </div>
      <p className="text-caption text-slate">{description}</p>
    </a>
  );
}

function HeroStat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-nano text-cool uppercase tracking-[0.12em]">{label}</div>
      <div className="mt-0.5 font-display text-body-lg font-semibold tabular-nums text-obsidian">{value}</div>
    </div>
  );
}
